import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  BookOpen, 
  Plus, 
  ChevronRight, 
  Clock,
  User,
  Trophy
} from 'lucide-react'

export default function LearningPaths() {
  const [paths, setPaths] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadPaths()
  }, [])

  async function loadPaths() {
    try {
      const res = await fetch('/api/learning-paths')
      if (res.ok) {
        const data = await res.json()
        setPaths(data.paths || [])
      }
    } catch (err) {
      console.error('Failed to load learning paths:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="page container" style={{ paddingTop: 'var(--space-2xl)' }}>
        <div className="animate-pulse text-secondary" style={{ textAlign: 'center' }}>
          Loading learning paths...
        </div>
      </div>
    )
  }

  return (
    <div className="page container">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: 'var(--space-2xl)' 
        }}
      >
        <div>
          <h1 style={{ marginBottom: 'var(--space-xs)' }}>Learning Paths</h1>
          <p className="text-secondary">Track your progress across subjects</p>
        </div>
        <button className="btn btn-primary">
          <Plus size={18} />
          Add Path
        </button>
      </motion.div>

      {/* Paths grid */}
      {paths.length > 0 ? (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: 'var(--space-lg)'
        }}>
          {paths.map((path, i) => (
            <motion.div
              key={path.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <PathCard path={path} />
            </motion.div>
          ))}
        </div>
      ) : (
        <EmptyState />
      )}
    </div>
  )
}

function PathCard({ path }) {
  const progress = path.total_modules > 0 
    ? Math.round((path.progress / path.total_modules) * 100) 
    : 0

  return (
    <div className="glass-card" style={{ 
      padding: 'var(--space-xl)',
      cursor: 'pointer'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'space-between',
        marginBottom: 'var(--space-lg)'
      }}>
        <div style={{
          width: 48,
          height: 48,
          borderRadius: 'var(--radius-md)',
          background: 'var(--accent-blue-muted)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <BookOpen size={24} style={{ color: 'var(--accent-blue)' }} />
        </div>
        <span style={{
          padding: 'var(--space-xs) var(--space-sm)',
          background: path.status === 'active' ? 'var(--accent-green-muted)' : 'var(--glass-bg)',
          color: path.status === 'active' ? 'var(--accent-green)' : 'var(--text-tertiary)',
          borderRadius: 'var(--radius-sm)',
          fontSize: '0.75rem',
          fontWeight: 500,
          textTransform: 'capitalize'
        }}>
          {path.status}
        </span>
      </div>

      <h3 style={{ marginBottom: 'var(--space-xs)' }}>{path.name}</h3>
      <p className="text-secondary" style={{ 
        fontSize: '0.875rem',
        marginBottom: 'var(--space-lg)'
      }}>
        {path.subject}
      </p>

      {/* Progress bar */}
      <div style={{ marginBottom: 'var(--space-md)' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          marginBottom: 'var(--space-xs)',
          fontSize: '0.875rem'
        }}>
          <span className="text-secondary">Progress</span>
          <span>{progress}%</span>
        </div>
        <div className="progress-bar">
          <div 
            className="progress-bar-fill" 
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Meta info */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 'var(--space-lg)',
        paddingTop: 'var(--space-md)',
        borderTop: '1px solid var(--glass-border)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
          <Clock size={14} className="text-tertiary" />
          <span className="text-tertiary" style={{ fontSize: '0.875rem' }}>
            {path.total_modules} modules
          </span>
        </div>
        {path.mentor_name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
            <User size={14} className="text-tertiary" />
            <span className="text-tertiary" style={{ fontSize: '0.875rem' }}>
              {path.mentor_name}
            </span>
          </div>
        )}
        <ChevronRight size={16} className="text-tertiary" style={{ marginLeft: 'auto' }} />
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="glass-card-static" style={{ 
      padding: 'var(--space-3xl)',
      textAlign: 'center'
    }}>
      <div style={{
        width: 80,
        height: 80,
        margin: '0 auto var(--space-lg)',
        borderRadius: '50%',
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Trophy size={36} className="text-tertiary" />
      </div>
      <h3 style={{ marginBottom: 'var(--space-sm)' }}>No learning paths yet</h3>
      <p className="text-secondary" style={{ 
        marginBottom: 'var(--space-lg)',
        maxWidth: 400,
        margin: '0 auto var(--space-lg)'
      }}>
        Start your learning journey by adding a path. Track subjects like Algebra, Chemistry, or any skill you want to master.
      </p>
      <button className="btn btn-primary">
        <Plus size={18} />
        Create Your First Path
      </button>
    </div>
  )
}
